import { Alert, Box, Paper, Stack, Typography } from '@mui/material';
import {
  DataView,
  Disclosure,
  duration,
  LongText,
  RawJson,
  record,
  StatusBadge,
  timestamp,
} from './DataView';
import { failureMessage } from './Timeline';
import type { AdminRun } from './types';

export default function RunDiagnostics({ run }: { run: AdminRun }) {
  const model = record(run.configuration)?.model;
  const error = failureMessage(run.error);
  return (
    <Paper
      component="article"
      aria-label={`Run ${run.id}`}
      variant="outlined"
      sx={{ p: 2, mb: 1.5 }}
    >
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="subtitle2">Run {run.id}</Typography>
        <StatusBadge status={run.status} />
        {typeof model === 'string' && (
          <Typography variant="caption" color="text.secondary">
            {model}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {timestamp(run.createdAt)}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
          bgcolor: 'grey.50',
          borderRadius: 1,
          p: 1.5,
          my: 1.5,
        }}
      >
        {[
          ['Model requests', run.requestCount],
          ['Duration', duration(run.totalDurationMs)],
          ['First text', duration(run.timeToFirstTextMs)],
          ['Queue wait', duration(run.queueWaitMs)],
        ].map(([label, value]) => (
          <Box key={label}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="body2">{value ?? 'Unavailable'}</Typography>
          </Box>
        ))}
      </Box>
      {(error || run.status === 'failed') && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <LongText
            text={error || 'Run failed. Open recorded errors for details.'}
          />
        </Alert>
      )}
      <Disclosure label="View run diagnostics">
        {[
          ['Configuration', run.configuration],
          ['Usage', run.usage],
          ['Steps', run.steps],
          ['Recorded errors', run.error],
          ['Client context', run.clientContext],
          ['Model inputs', run.inputItems],
        ].map(([label, value]) => (
          <Disclosure key={label as string} label={label as string}>
            <DataView value={value} />
          </Disclosure>
        ))}
        <RawJson value={run} />
      </Disclosure>
    </Paper>
  );
}
